/**
 * SearchStrictnessSlider — Settings control for preferences.searchStrictness (1–100).
 *
 * One track, three zones (Exact · Balanced · Broad). Drag or tap anywhere on the
 * track; the value commits on release, so the debounced profile save runs once.
 * Keyboard (web): arrows ±1, Shift/PageUp/PageDown ±10, Home/End. Screen readers:
 * an adjustable slider with the band in its spoken value. Springs collapse to
 * instant under reduced motion (utils/motion).
 *
 *   <SearchStrictnessSlider value={searchStrictness} onChange={setSearchStrictness} />
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { View, Text, Pressable, Animated, PanResponder, StyleSheet } from 'react-native';
import { IS_WEB, spring, webTransition } from '../utils/motion';
import {
    STRICTNESS_BANDS, STRICTNESS_DEFAULT, STRICTNESS_MIN, STRICTNESS_MAX,
    clampStrictness, strictnessAt, strictnessBandFor, strictnessForKey, strictnessFraction,
} from '../utils/searchStrictness';

const THUMB = 22;

export default function SearchStrictnessSlider({ value, onChange }) {
    const committed = clampStrictness(value ?? STRICTNESS_DEFAULT);
    const [draft, setDraft] = useState(committed);
    const [dragging, setDragging] = useState(false);
    const [width, setWidth] = useState(0);
    const pos = useRef(new Animated.Value(strictnessFraction(committed))).current;
    const lift = useRef(new Animated.Value(0)).current;
    const startX = useRef(0);
    const widthRef = useRef(0);
    const draftRef = useRef(committed);
    const onChangeRef = useRef(onChange);
    onChangeRef.current = onChange;

    // Outside changes (profile load, reset) move the thumb.
    useEffect(() => {
        if (dragging) return;
        draftRef.current = committed;
        setDraft(committed);
        spring(pos, strictnessFraction(committed), 'snappy', { useNative: false }).start();
    }, [committed]);

    useEffect(() => {
        spring(lift, dragging ? 1 : 0, 'snappy', { useNative: false }).start();
    }, [dragging]);

    const show = (v, animate) => {
        draftRef.current = v;
        setDraft(v);
        if (animate) spring(pos, strictnessFraction(v), 'snappy', { useNative: false }).start();
        else pos.setValue(strictnessFraction(v));
    };
    const commit = (v) => { if (v !== committed) onChangeRef.current?.(v); };

    const pan = useMemo(() => PanResponder.create({
        onStartShouldSetPanResponder: () => true,
        onMoveShouldSetPanResponder: () => true,
        onPanResponderTerminationRequest: () => false,
        onPanResponderGrant: (e) => {
            // The rail starts half a thumb in from the gesture layer's left edge.
            startX.current = e.nativeEvent.locationX - THUMB / 2;
            setDragging(true);
            show(strictnessAt(startX.current, widthRef.current), true);
        },
        onPanResponderMove: (_e, g) => show(strictnessAt(startX.current + g.dx, widthRef.current), false),
        onPanResponderRelease: () => { setDragging(false); commit(draftRef.current); },
        onPanResponderTerminate: () => { setDragging(false); commit(draftRef.current); },
    }), [committed]);

    const step = (next) => {
        if (next == null) return false;
        show(next, true);
        commit(next);
        return true;
    };

    const band = strictnessBandFor(draft);
    const left = pos.interpolate({ inputRange: [0, 1], outputRange: [0, Math.max(0, width)] });
    const thumbScale = lift.interpolate({ inputRange: [0, 1], outputRange: [1, 1.18] });
    const glow = lift.interpolate({ inputRange: [0, 1], outputRange: [0.35, 0.7] });
    const spoken = `${draft}, ${band.label}`;

    return (
        <View style={styles.wrap}>
            <View style={styles.header}>
                <View style={{ flex: 1, minWidth: 0 }}>
                    <Text style={styles.title}>Search Strictness</Text>
                    <Text style={styles.description}>How literally searches read what you type.</Text>
                </View>
                <View style={[styles.readout, { borderColor: `${band.color}66`, backgroundColor: `${band.color}1f` }, webTransition(['background-color', 'border-color'], 'fast')]}>
                    <Text style={[styles.readoutBand, { color: band.color }]}>{band.label}</Text>
                    <Text style={styles.readoutValue}>{draft}</Text>
                </View>
            </View>

            <View
                style={styles.trackHit}
                onLayout={(e) => { const w = e.nativeEvent.layout.width - THUMB; widthRef.current = w; setWidth(w); }}
                role="slider"
                aria-label="Search strictness"
                aria-valuemin={STRICTNESS_MIN}
                aria-valuemax={STRICTNESS_MAX}
                aria-valuenow={draft}
                aria-valuetext={spoken}
                accessibilityRole="adjustable"
                accessibilityLabel="Search strictness"
                accessibilityValue={{ min: STRICTNESS_MIN, max: STRICTNESS_MAX, now: draft, text: spoken }}
                accessibilityActions={[{ name: 'increment' }, { name: 'decrement' }]}
                onAccessibilityAction={(e) => step(clampStrictness(draft + (e.nativeEvent.actionName === 'increment' ? 5 : -5)))}
                focusable
                {...(IS_WEB ? {
                    tabIndex: 0,
                    onKeyDown: (e) => { if (step(strictnessForKey(draft, e.key, e.shiftKey))) e.preventDefault?.(); },
                } : {})}
            >
                <View style={styles.rail} pointerEvents="none">
                    {STRICTNESS_BANDS.map((b) => (
                        <View
                            key={b.key}
                            style={[
                                styles.zone,
                                { flex: b.to - b.from + 1, backgroundColor: `${b.color}${b.key === band.key ? '55' : '26'}` },
                                webTransition(['background-color'], 'fast'),
                            ]}
                        />
                    ))}
                </View>
                <Animated.View pointerEvents="none" style={[styles.fill, { width: left, backgroundColor: band.color }]} />
                <Animated.View
                    pointerEvents="none"
                    style={[styles.thumb, {
                        left,
                        borderColor: band.color,
                        transform: [{ scale: thumbScale }],
                        shadowColor: band.color,
                        shadowOpacity: glow,
                    }]}
                />
                {/* Gesture layer on top so locationX is always relative to the track. */}
                <View style={StyleSheet.absoluteFill} {...pan.panHandlers} />
            </View>

            <View style={styles.zoneLabels}>
                {STRICTNESS_BANDS.map((b) => {
                    const active = b.key === band.key;
                    const mid = Math.round((b.from + b.to) / 2);
                    return (
                        <Pressable
                            key={b.key}
                            onPress={() => step(b.key === 'balanced' ? STRICTNESS_DEFAULT : mid)}
                            style={({ hovered }) => [styles.zoneLabelBtn, { flex: b.to - b.from + 1 }, hovered && !active && { opacity: 0.85 }]}
                            accessibilityRole="button"
                            accessibilityLabel={`Set search strictness to ${b.label}`}
                        >
                            <Text style={[styles.zoneLabel, active && { color: b.color, fontWeight: '700' }, webTransition(['color'], 'fast')]}>{b.label}</Text>
                        </Pressable>
                    );
                })}
            </View>

            <View style={styles.footer}>
                <Text style={styles.summary} aria-live="polite">{band.summary}</Text>
                {committed !== STRICTNESS_DEFAULT ? (
                    <Pressable onPress={() => step(STRICTNESS_DEFAULT)} accessibilityRole="button" style={({ hovered }) => [styles.reset, hovered && styles.resetHover]}>
                        <Text style={styles.resetText}>Reset</Text>
                    </Pressable>
                ) : null}
            </View>
        </View>
    );
}

const styles = StyleSheet.create({
    wrap: { paddingVertical: 14, gap: 12 },
    header: { flexDirection: 'row', alignItems: 'flex-start', gap: 12 },
    title: { color: '#f1f5f9', fontSize: 15, fontWeight: '600' },
    description: { color: '#94a3b8', fontSize: 13, marginTop: 3, lineHeight: 18 },
    readout: {
        flexDirection: 'row', alignItems: 'baseline', gap: 8,
        paddingHorizontal: 12, paddingVertical: 6, borderRadius: 999, borderWidth: 1,
    },
    readoutBand: { fontSize: 13, fontWeight: '700', letterSpacing: 0.2 },
    readoutValue: { color: '#e2e8f0', fontSize: 13, fontVariant: ['tabular-nums'], minWidth: 22, textAlign: 'right' },
    trackHit: { height: 36, justifyContent: 'center', cursor: 'pointer', outlineOffset: 4, borderRadius: 10 },
    rail: {
        position: 'absolute', left: THUMB / 2, right: THUMB / 2, height: 6, borderRadius: 3,
        flexDirection: 'row', gap: 3, overflow: 'hidden',
    },
    zone: { height: 6 },
    fill: { position: 'absolute', left: THUMB / 2, height: 6, borderRadius: 3, opacity: 0.9 },
    thumb: {
        position: 'absolute', width: THUMB, height: THUMB, borderRadius: THUMB / 2,
        backgroundColor: '#0f172a', borderWidth: 3,
        shadowOffset: { width: 0, height: 0 }, shadowRadius: 10, elevation: 4,
    },
    zoneLabels: { flexDirection: 'row', paddingHorizontal: THUMB / 2 },
    zoneLabelBtn: { alignItems: 'center', paddingVertical: 2 },
    zoneLabel: { color: '#64748b', fontSize: 12, fontWeight: '500', letterSpacing: 0.3 },
    footer: { flexDirection: 'row', alignItems: 'flex-start', gap: 12 },
    summary: { flex: 1, color: '#cbd5e1', fontSize: 13, lineHeight: 19 },
    reset: { paddingHorizontal: 10, paddingVertical: 4, borderRadius: 8, borderWidth: 1, borderColor: 'rgba(148,163,184,0.35)' },
    resetHover: { backgroundColor: 'rgba(148,163,184,0.12)' },
    resetText: { color: '#cbd5e1', fontSize: 12, fontWeight: '600' },
});
